import type {
  HieuSuatDoanhThu,
  HieuSuatDoiTuong,
  HieuSuatNoiDung,
  HieuSuatTongQuan,
} from '@qlk/shared';
import { useQuery } from '@tanstack/react-query';

import { api, type LoiApi } from '../../lib/api.js';

/**
 * Hook cho bốn tab của "Hiệu suất".
 *
 * Mỗi tab một endpoint nên mở tab nào chỉ tải dữ liệu tab đó; `enabled` giữ
 * cho ba tab còn lại không gọi gì khi chưa bấm vào.
 */

const GOC = '/api/v1/analytics/kenh';

/*
 * Khoá cache gồm cả **mã kỳ**: hai kỳ có cùng khoảng ngày (tháng 10 bó ở
 * 26/10 và khoảng tự chọn 01–26/10) có kỳ trước khác nhau, nên là hai câu trả
 * lời khác nhau.
 */
export const KHOA_HIEU_SUAT = {
  tongQuan: (id: string, from: string, to: string, ma = '') =>
    ['analytics', 'hieu-suat', 'tong-quan', id, from, to, ma] as const,
  noiDung: (id: string, from: string, to: string, ma = '') =>
    ['analytics', 'hieu-suat', 'noi-dung', id, from, to, ma] as const,
  doiTuong: (id: string, from: string, to: string, ma = '') =>
    ['analytics', 'hieu-suat', 'doi-tuong', id, from, to, ma] as const,
  doanhThu: (id: string, from: string, to: string, ma = '') =>
    ['analytics', 'hieu-suat', 'doanh-thu', id, from, to, ma] as const,
};

interface Khoang {
  from: string;
  to: string;
  /**
   * **Mã kỳ** của khoảng (`KyTrang.ma`). Gửi kèm để máy chủ biết "kỳ trước" là
   * gì: tháng chưa trọn so với cùng số ngày đầu tháng trước, không phải với
   * 26 ngày liền trước. Thiếu mã thì máy chủ lùi đúng số ngày như trước.
   */
  ma?: string;
}

function duongDan(id: string, tab: string, k: Khoang): string {
  const q = new URLSearchParams({ from: k.from, to: k.to });
  if (k.ma !== undefined && k.ma !== '') q.set('ky', k.ma);
  return `${GOC}/${encodeURIComponent(id)}/${tab}?${q.toString()}`;
}

/** Khoảng chưa sẵn sàng (đang chờ mốc dữ liệu) thì chưa gọi gì. */
function batKhi(k: Khoang, bat: boolean): boolean {
  return bat && k.from !== '' && k.to !== '';
}

export function useHieuSuatTongQuan(channelId: string, k: Khoang, bat = true) {
  return useQuery<HieuSuatTongQuan, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.tongQuan(channelId, k.from, k.to, k.ma),
    queryFn: () => api.get<HieuSuatTongQuan>(duongDan(channelId, 'tong-quan', k)),
    enabled: batKhi(k, bat),
    staleTime: 5 * 60_000,
  });
}

export function useHieuSuatNoiDung(channelId: string, k: Khoang, bat = true) {
  return useQuery<HieuSuatNoiDung, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.noiDung(channelId, k.from, k.to, k.ma),
    queryFn: () => api.get<HieuSuatNoiDung>(duongDan(channelId, 'noi-dung', k)),
    enabled: batKhi(k, bat),
    staleTime: 5 * 60_000,
  });
}

export function useHieuSuatDoiTuong(channelId: string, k: Khoang, bat = true) {
  return useQuery<HieuSuatDoiTuong, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.doiTuong(channelId, k.from, k.to, k.ma),
    queryFn: () => api.get<HieuSuatDoiTuong>(duongDan(channelId, 'doi-tuong', k)),
    enabled: batKhi(k, bat),
    // Tab này có thể phải gọi YouTube (độ tuổi theo khoảng), giữ lâu hơn.
    staleTime: 30 * 60_000,
  });
}

export function useHieuSuatDoanhThu(channelId: string, k: Khoang, bat = true) {
  return useQuery<HieuSuatDoanhThu, LoiApi>({
    queryKey: KHOA_HIEU_SUAT.doanhThu(channelId, k.from, k.to, k.ma),
    queryFn: () => api.get<HieuSuatDoanhThu>(duongDan(channelId, 'doanh-thu', k)),
    enabled: batKhi(k, bat),
    staleTime: 5 * 60_000,
  });
}
